/**
 * E3G luteal high is 211.0 here and `E3G_LUTEAL_HIGH_THRESHOLD` is 221 in
 * oova-backend. The disagreement is known and deliberately unresolved — it needs
 * a clinical answer, not a code change.
 */

export type HormoneReferenceSeriesId = 'lh' | 'pg' | 'lr_e3g';

export type HormoneReferencePhaseId = 'follicular' | 'ovulation' | 'luteal';

export const HORMONE_REFERENCE_PHASE_ORDER: HormoneReferencePhaseId[] = [
  'follicular',
  'ovulation',
  'luteal',
];

export const HORMONE_REFERENCE_PHASE_LABEL_KEYS: Record<
  HormoneReferencePhaseId,
  string
> = {
  follicular: 'hormoneTrends.referenceRanges.phases.follicular',
  ovulation: 'hormoneTrends.referenceRanges.phases.ovulation',
  luteal: 'hormoneTrends.referenceRanges.phases.luteal',
};

export interface HormoneReferenceRange {
  low: number;
  high: number;
}

export interface HormoneReferenceRangeSet {
  id: HormoneReferenceSeriesId;
  displayName: string;
  expansionKey?: string;
  unit: string;
  ranges: Record<HormoneReferencePhaseId, HormoneReferenceRange>;
}

export const HORMONE_REFERENCE_RANGES: Record<
  HormoneReferenceSeriesId,
  HormoneReferenceRangeSet
> = {
  lh: {
    id: 'lh',
    displayName: 'LH',
    unit: 'mIU/L',
    ranges: {
      follicular: { low: 2.4, high: 12.6 },
      ovulation: { low: 14.0, high: 95.6 },
      luteal: { low: 1.0, high: 11.4 },
    },
  },
  pg: {
    id: 'pg',
    displayName: 'PdG',
    expansionKey: 'hormoneTrends.referenceRanges.expansions.pg',
    unit: 'ng/mL',
    ranges: {
      follicular: { low: 0.1, high: 0.9 },
      ovulation: { low: 0.1, high: 12.0 },
      luteal: { low: 1.8, high: 23.9 },
    },
  },
  lr_e3g: {
    id: 'lr_e3g',
    displayName: 'E3G',
    expansionKey: 'hormoneTrends.referenceRanges.expansions.lrE3g',
    unit: 'ng/mL',
    ranges: {
      follicular: { low: 12.5, high: 166.0 },
      ovulation: { low: 85.8, high: 498.0 },
      luteal: { low: 43.8, high: 211.0 },
    },
  },
};

export const HORMONE_REFERENCE_SERIES_ORDER: HormoneReferenceSeriesId[] = [
  'lh',
  'pg',
  'lr_e3g',
];
